// src/components/Home.jsx
import React, { useState, useRef } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import { CheckCircle2, Star, Play, Users, Calendar, Pill, BarChart3, CreditCard, Shield } from "lucide-react";
import { SEO } from "../utils/SEO";
import PlanSelectionModal from "../components/PlanSelectionModal";
import PRICING from "../utils/PRICING.json";
import en from "../translations/en";
import fr from "../translations/fr";
import ar from "../translations/ar";

// Reusable fade-up animation wrapper
const FadeUp = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 32 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.65, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// Map feature titles to Lucide icons
const getFeatureIcon = (title) => {
  const iconProps = { size: 48, className: "text-teal-600" };
  const titleLower = title.toLowerCase();
  
  if (titleLower.includes("patient")) return <Users {...iconProps} />;
  if (titleLower.includes("schedul") || titleLower.includes("appointment")) return <Calendar {...iconProps} />;
  if (titleLower.includes("prescri")) return <Pill {...iconProps} />;
  if (titleLower.includes("analytic") || titleLower.includes("analytics")) return <BarChart3 {...iconProps} />;
  if (titleLower.includes("billing") || titleLower.includes("insurance")) return <CreditCard {...iconProps} />;
  if (titleLower.includes("hipaa") || titleLower.includes("complian")) return <Shield {...iconProps} />;
  return <Users {...iconProps} />;
};

const Home = () => {
  const language = useSelector((state) => state.language);
  const navigate = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedPlanNotification, setSelectedPlanNotification] = useState(null);
  const [billingCycle, setBillingCycle] = useState("monthly");
  
  // Hero scroll animations
  const heroRef = useRef(null);
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 300], [0, 100]);
  const heroOpacity = useTransform(scrollY, [0, 300], [1, 0]);

  // Navigate to pricing page
  const handleGoToPricing = () => {
    navigate("/pricing");
  };

  // Load selected plan from localStorage on mount
  React.useEffect(() => {
    const savedPlan = localStorage.getItem("selectedPlan");
    if (savedPlan) {
      setSelectedPlanNotification(JSON.parse(savedPlan));
    }
  }, []);

  const handleSelectPlan = (planName, planIndex) => {
    // If plan is already selected, unselect it
    if (selectedPlanNotification && selectedPlanNotification.index === planIndex) {
      localStorage.removeItem("selectedPlan");
      setSelectedPlanNotification(null);
    } else {
      // Select the plan
      setSelectedPlan({ planName, planIndex });
      setShowModal(true);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedPlan(null);
  };

  // Get translations and pricing based on selected language
  const getTranslations = () => {
    switch (language) {
      case "fr":
        return fr;
      case "ar":
        return ar;
      default:
        return en;
    }
  };

  const getPricingPlans = () => {
    switch (language) {
      case "fr":
        return Object.values(PRICING.fr.plans);
      case "ar":
        return Object.values(PRICING.ar.plans);
      default:
        return Object.values(PRICING.en.plans);
    }
  };

  const t = getTranslations();
  const plans = getPricingPlans();

  return (
    <>
      <SEO 
        title="RAQIB - Healthcare Clinic Management System for Morocco"
        description="Professional healthcare management platform for Moroccan clinics. Secure, HIPAA compliant patient records, appointment scheduling, and billing. Trusted by leading healthcare providers."
        keywords="healthcare management Morocco, clinic management system, medical records, patient scheduling, healthcare software Morocco, medical practice management"
        url="https://www.raqibapp.com"
        language={language}
      />
      <div className="min-h-screen bg-gradient-to-br from-white via-blue-50 to-indigo-50 text-gray-800 font-sans" dir={language === "ar" ? "rtl" : "ltr"}>
      <main>
        {/* ─── Hero ─── */}
        <section
          ref={heroRef}
          className="relative overflow-hidden min-h-[80vh] sm:min-h-screen flex items-center justify-center px-4 py-24"
          style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #0f172a 100%)" }}
        >
          {/* Animated background blobs */}
          <motion.div
            animate={{ scale: [1, 1.15, 1], opacity: [0.18, 0.28, 0.18] }}
            transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
            className="absolute top-[-10%] left-[-5%] w-[400px] h-[400px] rounded-full pointer-events-none"
            style={{ background: "radial-gradient(circle, #10B981 0%, transparent 70%)" }}
          />
          <motion.div
            animate={{ scale: [1, 1.2, 1], opacity: [0.12, 0.22, 0.12] }}
            transition={{ duration: 9, repeat: Infinity, ease: "easeInOut", delay: 2 }}
            className="absolute bottom-[-15%] right-[-5%] w-[500px] h-[500px] rounded-full pointer-events-none"
            style={{ background: "radial-gradient(circle, #6366F1 0%, transparent 70%)" }}
          />
          {/* Grid pattern overlay */}
          <div
            className="absolute inset-0 opacity-[0.04] pointer-events-none"
            style={{
              backgroundImage: `linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)`,
              backgroundSize: "60px 60px",
            }}
          />

          <motion.div style={{ y: heroY, opacity: heroOpacity }} className="relative z-10 text-center max-w-4xl mx-auto">
            <motion.span
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-block text-xs font-black tracking-[0.25em] uppercase px-4 py-2 rounded-full mb-8 border"
              style={{ color: "#10B981", borderColor: "#10B98140", background: "#10B98110" }}
            >
              {language === "ar" ? "حلولنا" : language === "fr" ? "Nos Solutions" : "Our Solutions"}
            </motion.span>

            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
              className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-black text-white mb-6 leading-[1.05] tracking-tight"
              style={{ fontFamily: "'DM Serif Display', serif" }}
            >
              {language === "ar" ? "راقب يحل" : language === "fr" ? "Raqib Résout" : "Raqib Solves"}
              <br />
              <span style={{ color: "#10B981" }}>
                {language === "ar" ? "المشاكل الحقيقية" : language === "fr" ? "Des Problèmes Réels" : "Real Problems"}
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.22 }}
              className="text-base sm:text-lg md:text-xl text-slate-400 max-w-2xl mx-auto leading-relaxed font-light"
            >
              {language === "ar"
                ? "يتكيف الرقيب مع سير عملك - سواء كنت تدير عيادة أو تتعامل مع المرضى أو تضمن أمان النظام. اكتشف كيف يحل الرقيب المشاكل الحقيقية في بيئات الرعاية الصحية."
                : language === "fr"
                ? "Raqib s'adapte à votre flux de travail — que vous gériez une clinique, traitiez des patients ou assuriez la sécurité du système. Découvrez comment Raqib résout les vrais problèmes dans les environnements de soins de santé."
                : "Raqib adapts to your workflow — whether you're managing a clinic, handling patients, or ensuring system security. Discover how Raqib solves real problems in healthcare environments."}
            </motion.p>

            {/* Scrolling indicator */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1 }}
              className="mt-16 flex justify-center"
            >
              <motion.div
                animate={{ y: [0, 10, 0] }}
                transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                className="flex flex-col items-center gap-2 text-slate-500"
              >
                <span className="text-xs tracking-widest uppercase font-semibold">
                  {language === "ar" ? "اسحب" : language === "fr" ? "Défiler" : "Scroll"}
                </span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </motion.div>
            </motion.div>
          </motion.div>
        </section>

        {/* Trusted By Section */}
        {/* <section className="py-12 bg-gray-50">
          <div className="container mx-auto px-4">
            <p className="text-center text-gray-500 mb-8">
              Trusted by leading healthcare providers
            </p>
            <div className="flex flex-wrap justify-center items-center gap-12 opacity-70">
              <div className="text-2xl font-bold text-blue-800">MediCare</div>
              <div className="text-2xl font-bold text-teal-600">HealthPlus</div>
              <div className="text-2xl font-bold text-purple-600">
                City Clinic
              </div>
              <div className="text-2xl font-bold text-blue-600">
                Prime Hospital
              </div>
            </div>
          </div>
        </section> */}

        {/* Features Section */}
        <section className="py-20" dir={language === "ar" ? "rtl" : "ltr"}>
          <div className="container mx-auto px-4">
            <FadeUp className="text-center mb-16">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                {t.features.sectionTitle}
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto text-lg">
                {t.features.sectionDescription}
              </p>
            </FadeUp>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {t.features.items.map((feature, index) => (
                <FadeUp key={index} delay={0.1 * index} className="h-full">
                  <div className="bg-white p-8 rounded-xl shadow-md hover:shadow-lg transition-all border border-gray-100 hover:border-teal-200 h-full">
                    <div className="mb-6">
                      {getFeatureIcon(feature.title)}
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                    <p className="text-gray-600">{feature.description}</p>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
        </section>

        {/* Demo Video Section */}
        <section className="py-20 bg-gradient-to-br from-white via-teal-50 to-blue-50" dir={language === "ar" ? "rtl" : "ltr"}>
          <div className="container mx-auto px-4 flex flex-col md:flex-row items-center gap-6 sm:gap-8 md:gap-12">
            <FadeUp className="md:w-1/2">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-6">{t.demoVideo.title}</h2>
              <p className="text-gray-600 mb-8 text-lg">
                {t.demoVideo.description}
              </p>
              <ul className="space-y-4">
                {t.demoVideo.benefits.map((benefit, index) => (
                  <FadeUp key={index} delay={0.05 * index} className="flex items-start">
                    <div className="bg-teal-100 text-teal-600 p-2 rounded-full mr-4 mt-1 flex-shrink-0">
                      <CheckCircle2 size={20} />
                    </div>
                    <span className="text-gray-700 font-medium">{benefit}</span>
                  </FadeUp>
                ))}
              </ul>
            </FadeUp>
            <FadeUp delay={0.2} className="md:w-1/2">
              <div className="relative w-full max-w-2xl bg-gradient-to-br from-teal-100 to-blue-100 rounded-2xl overflow-hidden shadow-2xl aspect-video flex items-center justify-center transform hover:scale-105 transition-transform">
                <div className="absolute inset-0 flex items-center justify-center">
                  <motion.div 
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    className="w-16 h-16 sm:w-20 sm:h-20 bg-white rounded-full flex items-center justify-center cursor-pointer shadow-lg"
                  >
                    <Play size={40} className="text-teal-600 ml-1" fill="currentColor" />
                  </motion.div>
                </div>
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-4 text-white">
                  <div className="text-sm font-semibold">{t.demoVideo.videoDemoTitle}</div>
                </div>
              </div>
            </FadeUp>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-20 bg-gradient-to-r from-teal-500 via-blue-500 to-blue-600 text-white overflow-hidden relative" dir={language === "ar" ? "rtl" : "ltr"}>
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full mix-blend-multiply filter blur-3xl"></div>
            <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full mix-blend-multiply filter blur-3xl"></div>
          </div>
          <div className="container mx-auto px-4 relative z-10">
            <FadeUp className="text-center mb-16">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4">
                {t.testimonials.sectionTitle}
              </h2>
            </FadeUp>

            <div className="grid md:grid-cols-3 gap-8">
              {t.testimonials.items.map((testimonial, index) => (
                <FadeUp key={index} delay={0.1 * index} className="h-full">
                  <div className="bg-white bg-opacity-10 backdrop-filter backdrop-blur-md p-8 rounded-xl border border-white border-opacity-20 hover:bg-opacity-20 transition-all h-full">
                    <div className="flex items-center mb-6">
                      <div className="w-14 h-14 rounded-full bg-gradient-to-r from-green-300 to-teal-300 flex items-center justify-center text-blue-800 font-bold mr-4 text-lg">
                        {testimonial.initials}
                      </div>
                      <div>
                        <h4 className="font-bold text-lg">{testimonial.name}</h4>
                        <p className="text-blue-100 text-sm">
                          {testimonial.title}
                        </p>
                      </div>
                    </div>
                    <p className="italic text-blue-50 mb-4 leading-relaxed">
                      "{testimonial.text}"
                    </p>
                    <div className="flex">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          size={20}
                          className={i < testimonial.rating ? "text-yellow-300 fill-current" : "text-gray-300 opacity-50"}
                        />
                      ))}
                    </div>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing */}
        <section className="py-20 bg-white" dir={language === "ar" ? "rtl" : "ltr"}>
          <div className=" container mx-auto px-4">
            <FadeUp className="text-center mb-16">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                {t.pricing.sectionTitle}
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto text-lg">
                {t.pricing.description}
              </p>
            </FadeUp>

            {/* Billing Cycle Toggle */}
            <FadeUp delay={0.1} className="flex justify-center mb-8">
              <div className="inline-flex bg-gray-200 rounded-full p-1 shadow-md">
                <button
                  onClick={() => setBillingCycle("monthly")}
                  className={`px-6 py-2 rounded-full font-semibold transition-all ${
                    billingCycle === "monthly"
                      ? "bg-teal-500 text-white shadow-md"
                      : "text-gray-700 hover:text-gray-900"
                  }`}
                >
                  {language === "ar" ? "شهري" : language === "fr" ? "Mensuel" : "Monthly"}
                </button>
                <button
                  onClick={() => setBillingCycle("yearly")}
                  className={`px-6 py-2 rounded-full font-semibold transition-all relative ${
                    billingCycle === "yearly"
                      ? "bg-teal-500 text-white shadow-md"
                      : "text-gray-700 hover:text-gray-900"
                  }`}
                >
                  {language === "ar" ? "سنوي" : language === "fr" ? "Annuel" : "Yearly"}
                  {billingCycle === "yearly" && (
                    <span className="absolute -top-2 -right-2 bg-gradient-to-r from-green-400 to-green-500 text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg">
                      {language === "ar" ? "-10%" : language === "fr" ? "-10%" : "-10%"}
                    </span>
                  )}
                </button>
              </div>
            </FadeUp>

            {/* Selected Plan Notification - At top of pricing section */}
            {selectedPlanNotification && (
              <FadeUp delay={0.15} className="bg-gradient-to-r from-green-400 to-teal-500 text-white py-4 px-6 rounded-lg mb-12 shadow-lg max-w-4xl mx-auto">
                <div className="flex justify-between items-start flex-wrap gap-4">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-lg">
                      {language === "ar" 
                        ? `✓ تم اختيار الخطة: ${selectedPlanNotification.nameAr || selectedPlanNotification.name}`
                        : language === "fr"
                        ? `✓ Plan sélectionné: ${selectedPlanNotification.nameFr || selectedPlanNotification.name}`
                        : `✓ Selected Plan: ${selectedPlanNotification.nameEn || selectedPlanNotification.name}`
                      }
                    </p>
                    <p className="text-sm opacity-90 mt-1">
                      {language === "ar" 
                        ? selectedPlanNotification.descAr || selectedPlanNotification.description
                        : language === "fr"
                        ? selectedPlanNotification.descFr || selectedPlanNotification.description
                        : selectedPlanNotification.descEn || selectedPlanNotification.description
                      }
                    </p>
                    <p className="text-sm opacity-90 mt-2">
                      {language === "ar" 
                        ? `سعر: ${selectedPlanNotification.billingCycle === "yearly" ? (selectedPlanNotification.price * 0.9).toFixed(2) : selectedPlanNotification.price} ${selectedPlanNotification.currency}`
                        : language === "fr"
                        ? `Prix: ${selectedPlanNotification.billingCycle === "yearly" ? (selectedPlanNotification.price * 0.9).toFixed(2) : selectedPlanNotification.price} ${selectedPlanNotification.currency}`
                        : `Price: ${selectedPlanNotification.billingCycle === "yearly" ? (selectedPlanNotification.price * 0.9).toFixed(2) : selectedPlanNotification.price} ${selectedPlanNotification.currency}`
                      }
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setSelectedPlanNotification(null);
                      }}
                      className="bg-white hover:bg-gray-200 text-green-600 font-semibold px-4 py-2 rounded-lg transition whitespace-nowrap text-sm"
                    >
                      {language === "ar" ? "تغيير الخطة" : language === "fr" ? "Changer le plan" : "Change Plan"}
                    </button>
                    <button
                      onClick={() => setSelectedPlanNotification(null)}
                      className="text-white hover:text-gray-200 text-2xl transition"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              </FadeUp>
            )}

            <div className="grid md:grid-cols-4 gap-8 w-full auto-rows-fr mt-20">
              {plans.map((plan, index) => {
                const isSelected = selectedPlanNotification && selectedPlanNotification.index === index;
                return (
                  <div
                    key={index}
                    className={`border transition-all duration-300 rounded-xl p-8 relative flex flex-col ${
                      isSelected
                        ? "border-4 border-green-500 bg-green-50 shadow-2xl transform scale-110"
                        : plan.recommended
                        ? "border-2 border-teal-500 bg-gray-50 transform scale-105 shadow-lg"
                        : "border-gray-200"
                    } hover:border-teal-500 transition-colors`}
                  >
                    {isSelected && (
                      <div className="absolute -top-2 -right-2 bg-green-500 text-white text-xs font-bold px-3 py-1.5 rounded-bl-lg rounded-tr-lg shadow-lg z-20">
                        {language === "ar" ? "✓ مختار" : language === "fr" ? "✓ SÉLECTIONNÉ" : "✓ SELECTED"}
                      </div>
                    )}
                    {plan.recommended && !isSelected && (
                      <div className="absolute -top-2 -right-2 bg-teal-500 text-white text-xs font-bold px-3 py-1.5 rounded-bl-lg rounded-tr-lg shadow-lg z-20">
                        {language === "ar" ? "مُوصى به" : language === "fr" ? "RECOMMANDÉ" : "RECOMMENDED"}
                      </div>
                    )}
                    <h3 className="text-xl font-bold mb-2">{plan.name}</h3>
                    <p className="text-gray-600 mb-6 text-sm">{plan.description}</p>
                    {plan.notes && (
                      <p className="text-xs text-gray-500 mb-4 italic bg-gray-100 p-2 rounded">
                        {plan.notes}
                      </p>
                    )}
                  <div className="mb-6">
                      {plan.price !== null ? (
                        <>
                          <span className="text-4xl font-bold">{billingCycle === "yearly" ? (plan.price * 0.9).toFixed(2) : plan.price}</span>
                          <span className="text-gray-500"> {plan.currency}</span>
                          <div className="text-sm text-gray-500">
                            {billingCycle === "yearly" 
                              ? (language === "ar" ? "سنويًا" : language === "fr" ? "Annuellement" : "Yearly")
                              : (language === "ar" ? "شهريًا" : language === "fr" ? "Mensuellement" : "Monthly")
                            }
                          </div>
                          {billingCycle === "yearly" && plan.price !== null && (
                            <div className="text-xs text-green-600 font-semibold mt-1">
                              {language === "ar" ? "تم توفير 10%" : language === "fr" ? "Économiser 10%" : "You save 10%"}
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="text-3xl font-bold text-teal-600">
                          {language === "ar" ? "تسعير مخصص" : language === "fr" ? "Tarification personnalisée" : "Custom Pricing"}
                        </div>
                      )}
                    </div>
                    <div className="mb-6">
                      <h4 className="font-semibold text-sm mb-3 text-gray-700">
                        {language === "ar" ? "الميزات:" : language === "fr" ? "Fonctionnalités:" : "Features:"}
                      </h4>
                      <ul className="space-y-2 text-sm">
                        {Object.values(plan.features).map((feature, i) => {
                          const isEnabled = feature.value === true || (typeof feature.value === 'string' && feature.value !== false);
                          return (
                            <li
                              key={i}
                              className={`flex items-start ${
                                isEnabled
                                  ? "text-gray-700"
                                  : "text-gray-400"
                              }`}
                            >
                              {isEnabled ? (
                                <svg
                                  className={`w-4 h-4 text-teal-500 mt-0.5 flex-shrink-0 ${
                                    language === "ar" ? "ml-2" : "mr-2"
                                  }`}
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M5 13l4 4L19 7"
                                  ></path>
                                </svg>
                              ) : (
                                <svg
                                  className={`w-4 h-4 text-gray-300 mt-0.5 flex-shrink-0 ${
                                    language === "ar" ? "ml-2" : "mr-2"
                                  }`}
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M6 18L18 6M6 6l12 12"
                                  ></path>
                                </svg>
                              )}
                              <div className="flex flex-col">
                                <span className={isEnabled ? "" : "line-through"}>
                                  {feature.name}
                                </span>
                                {feature.description && (
                                  <span className="text-xs opacity-70">
                                    {feature.description}
                                  </span>
                                )}
                                {typeof feature.value === 'string' && feature.value !== false && (
                                  <span className="text-xs font-semibold text-teal-600 mt-1">
                                    ({feature.value})
                                  </span>
                                )}
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                    <button
                      onClick={() => handleSelectPlan(plan.name, index)}
                      dir={language === "ar" ? "rtl" : "ltr"}
                      className={`w-full py-3 px-4 rounded-lg transition-colors text-sm font-semibold mt-auto ${
                        isSelected
                          ? "bg-green-500 hover:bg-green-600 text-white"
                          : plan.recommended
                          ? "bg-teal-500 hover:bg-teal-600 text-white"
                          : "bg-gray-100 hover:bg-gray-200 text-gray-800"
                      }`}
                    >
                      {isSelected 
                        ? (language === "ar" ? "✓ مختار" : language === "fr" ? "✓ Sélectionné" : "✓ Selected")
                        : (language === "ar" ? "اختر الخطة" : language === "fr" ? "Sélectionner le plan" : "Select Plan")
                      }
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-teal-500 via-blue-500 to-blue-600 text-white overflow-hidden relative" dir={language === "ar" ? "rtl" : "ltr"}>
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full mix-blend-multiply filter blur-3xl"></div>
            <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full mix-blend-multiply filter blur-3xl"></div>
          </div>
          <div className="container mx-auto px-4 text-center relative z-10">
            <FadeUp>
              <h2 className="text-4xl font-bold mb-6">
                {t.cta.title}
              </h2>
              <p className="text-xl mb-8 max-w-2xl mx-auto text-blue-100">
                {t.cta.description}
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <motion.button 
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleGoToPricing}
                  className="bg-white hover:bg-green-50 text-teal-600 font-bold py-3 px-8 rounded-full transition-all shadow-lg"
                >
                  {t.cta.buttonStartTrial}
                </motion.button>
                <motion.button 
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-transparent hover:bg-blue-700 text-white font-semibold py-3 px-8 border-2 border-white rounded-full transition-all"
                >
                  {t.cta.buttonScheduleDemo}
                </motion.button>
              </div>
            </FadeUp>
          </div>
        </section>
      </main>

      <PlanSelectionModal
        plan={selectedPlan}
        isOpen={showModal}
        onClose={handleCloseModal}
        billingCycle={billingCycle}
        onPlanSelected={(planInfo) => setSelectedPlanNotification(planInfo)}
      />
      </div>
    </>
  );
};

export default Home;
